/**
 * THE COMMISSIONER'S BIDDING TABLE (Tim, 8 September: "for commissioner and admin: give me a
 * vertical table representing all GMs vertically with +1 / +5 / +10 / [enter Bid][Bid] options
 * across the row. be responsive to existing bids: leading bidder can't make another bid, clearly
 * identify in real-time who's leading the bids with what number bid").
 *
 * On the night, ten men call numbers across a room and one person types them. The rail used to
 * carry a second list of the same ten men — `Who is bidding` — with one button each offering
 * exactly one amount, so "Rajan says twelve" over a standing eight was four presses, each of
 * which reset the going-gone clock. This is the same act with the amount in it, and it is now the
 * ONLY list of the room (Tim, 9 September: "remove 'who is bidding' and combine any lost function
 * into the panel labeled 'on Nate Frazier'... That should be my active bid tracking panel and
 * commissioner action panel"). Two lists of ten names on one screen, disagreeing about their
 * order, was the room asking which one to read.
 *
 * SO IT IS NOT COMMISSIONER-ONLY ANY MORE. It was, while the rail carried the reading version for
 * everybody else; with the rail's copy gone, a GM who could not see this panel could not see the
 * room at all. `canBidFor` decides per row who gets controls — a commissioner types for everyone,
 * a GM only for himself — and the standings column is the same for all ten.
 *
 * THE ROWS DO NOT MOVE. The rail sorts by bid, which is right for reading and wrong for typing:
 * a table that reorders itself between a commissioner looking at a row and pressing it will
 * eventually record the wrong man's bid. Here the order is the nomination order, all evening, and
 * the leader is shown by marking his row rather than by lifting it to the top.
 *
 * WHAT IT REFUSES, IT REFUSES BY NOT OFFERING. The leader has no controls at all — he cannot bid
 * against himself (`guardRaise`, and `firestore.rules` agrees), and with a clock that resets on
 * every raise a mis-tap on his row would also extend a lot he had already won. A GM whose farm is
 * full keeps his row and loses his buttons, with the reason written where the buttons were.
 */

import { useEffect, useState } from 'react';
import { faab } from '../domain/faab.js';
import { managerShort } from '../domain/identity.js';
import { raiseSteps } from '../domain/farmAuction.js';
import { GmBadge } from './GmBadge.js';

export interface BidRow {
  gm: string;
  /** He is out of this lot: a full farm, or a win already taken in this round. */
  full: boolean;
  /**
   * Why, in the words of the rule that says so.
   *
   * TWO BLOCKS THAT ARE NOT THE SAME THING (Tim, 9 September). A full farm is out of the auction
   * for good; a win already taken this round is out until the next round starts. A man shown the
   * wrong one argues about it, and on draft night he argues out loud.
   */
  blockedWhy?: string;
  /** He has taken himself out of this lot. */
  passed: boolean;
  /**
   * What he has left to spend.
   *
   * A COLUMN OF ITS OWN (Tim, 9 September: "make remaining FAAB more prominent in its own column
   * per GM"). It rode the name in 10.5px grey, which put the one number that decides whether a bid
   * is legal — 2025-proposal-01-faab-farm-bids §1 — in the row's smallest type, wrapped in a name
   * that could be seven characters or twelve. Its own right-aligned column makes ten balances a
   * column a commissioner can read down while the room calls numbers.
   *
   * It still gates nothing. The rule is the room's to keep; the screen's job is to make the figure
   * impossible to miss rather than to referee it.
   */
  left: number;
}

export function BidTable(
  { rows, nextBid, high, live, canBidFor, onRaise, onPass, onUnpass }: {
    rows: readonly BidRow[];
    /** The smallest legal bid: the standing one plus one, or the opening bid. */
    nextBid: number;
    high: { gm: string; amount: number } | null;
    /** False while nothing is up for bid — the table stays, inert, so the page does not jump. */
    live: boolean;
    /** May the viewer act on this man's row? A commissioner may on all ten, a GM on his own. */
    canBidFor?: (gm: string) => boolean;
    onRaise: (to: number, gm: string) => void;
    onPass: (gm: string) => void;
    onUnpass: (gm: string) => void;
  },
) {
  /*
   * ONE TYPED FIELD AT A TIME, and it empties itself the moment the standing bid moves. A number
   * left in a box across two bids is a number somebody typed against a price that no longer
   * exists — the guard would refuse it, but only after it had been sent in a man's name.
   */
  const [typed, setTyped] = useState<Record<string, string>>({});
  useEffect(() => { setTyped({}); }, [high?.gm, high?.amount, live]);

  const steps = raiseSteps(nextBid);
  const bid = (gm: string) => {
    const to = Number(typed[gm]);
    if (!Number.isInteger(to) || to < nextBid) return;
    onRaise(to, gm);
    setTyped((t) => ({ ...t, [gm]: '' }));
  };

  return (
    <div className="bidtable">
      <div className="bthead">
        <span className="btwho">Manager</span>
        <span className="btmoney">FAAB left</span>
        <span className="btstate">Standing</span>
        <span className="btacts">
          {live
            ? <>Raise to at least <b>{faab(nextBid)}</b></>
            : 'Nothing is up for bid'}
        </span>
      </div>
      <ul className="btrows">
        {rows.map((r) => {
          const gm = r.gm.toUpperCase();
          const leads = high?.gm === gm;
          /* Not his to press: he keeps the row and the standing, and loses the controls. */
          const mine = canBidFor ? canBidFor(r.gm) : true;
          const out = !live || r.full || leads || !mine;
          const value = typed[gm] ?? '';
          return (
            <li key={r.gm} className={`btrow${leads ? ' leads' : ''}${r.full ? ' full' : ''}`}>
              <span className="btwho">
                <GmBadge gm={r.gm} />
                <b>{managerShort(r.gm)}</b>
              </span>

              {/*
                THE NUMBER THAT DECIDES THE BID, in the type a number that matters gets. `low` is
                not a rule and does not gate anything — it is the man who cannot cover the next
                raise, marked so the commissioner sees it before he types rather than after.
              */}
              <b className={`btmoney${r.left < nextBid ? ' low' : ''}`}
                title={r.left < nextBid
                  ? `${managerShort(r.gm)} cannot cover ${faab(nextBid)}`
                  : `${managerShort(r.gm)} has ${faab(r.left)} to spend`}>
                {faab(r.left)}
              </b>

              {/*
                THE ONE THING EVERY MAN IN THE ROOM IS ASKING. The leader's row says his number
                rather than the word "leading", because the number is the question — and it is the
                only bold thing in the column, so the eye finds it without reading the names.
              */}
              <span className="btstate">
                {leads ? <b className="btlead">leads at {faab(high!.amount)}</b>
                  : r.full
                    ? <span className="mut">{/won this round/i.test(r.blockedWhy ?? '') ? 'won this round' : 'farm full'}</span>
                    : r.passed ? <span className="mut">passed</span>
                      : <span className="mut" aria-hidden="true">—</span>}
              </span>

              <span className="btacts">
                {leads ? (
                  <span className="mut btnote">Cannot bid against himself</span>
                ) : r.full ? (
                  <span className="mut btnote">
                    {r.blockedWhy ?? 'Out of this lot'}
                  </span>
                ) : !mine ? (
                  <span className="mut btnote" aria-hidden="true" />
                ) : (
                  <>
                    {steps.map(({ step, to }) => (
                      <button type="button" className="btn small" key={step} disabled={out}
                        title={`${managerShort(r.gm)} bids ${faab(to)}`}
                        onClick={() => onRaise(to, r.gm)}>
                        +{faab(step)}
                      </button>
                    ))}
                    {/*
                      The field takes the number a man actually called. `min` is the smallest legal
                      bid rather than zero, so the control refuses what `guardRaise` would refuse
                      anyway — before it is sent, and before the clock resets.
                    */}
                    <input type="number" className="btfield" inputMode="numeric"
                      min={nextBid} step={1} value={value} disabled={out}
                      placeholder={String(nextBid)}
                      aria-label={`Bid for ${managerShort(r.gm)}`}
                      onChange={(e) => setTyped((t) => ({ ...t, [gm]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') bid(gm); }} />
                    <button type="button" className="btn small primary" disabled={out || !value}
                      title={`${managerShort(r.gm)} bids ${value ? faab(Number(value)) : ''}`}
                      onClick={() => bid(gm)}>
                      Bid
                    </button>
                    <button type="button" className="btn small ghost" disabled={!live || !mine}
                      title={r.passed ? `${managerShort(r.gm)} is back in` : `${managerShort(r.gm)} passes`}
                      onClick={() => (r.passed ? onUnpass(r.gm) : onPass(r.gm))}>
                      {r.passed ? 'back in' : 'pass'}
                    </button>
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
